import React, { useMemo } from 'react';
import ColorSwatch from 'components/commercetools-ui/atoms/color-swatch';
import { HorizontalMore } from 'components/commercetools-ui/organisms/mini-cart';
import { AttributeMapper } from 'components/commercetools-ui/organisms/product/product-details/helpers/attributeMapper';
import { discardRepeatedValues } from 'components/commercetools-ui/organisms/product/product-details/helpers/discardRepeatedValues';
import { getAttributeLabel, getAttributeValue } from 'helpers/utils/attributes';
import { Variant, Product as ProductType } from 'types/entity/product';

type ColorSwatchesProps = {
  product: ProductType;
  selectedVariant: Variant;
  currentVariantUpdate: (variant: Variant) => void;
  attributesToBeConsidered?: string[];
};

export const MAX_SWATCHES_TO_DISPLAY = 5;

const ColorSwatches: React.FC<ColorSwatchesProps> = ({
  product,
  selectedVariant,
  currentVariantUpdate,
  attributesToBeConsidered,
}) => {
  const subCategoryKey = product?.categories?.[0]?.categoryKey || '';
  const attributesToDisplay = AttributeMapper(selectedVariant, subCategoryKey, attributesToBeConsidered);

  let currentVariantSelection: Record<string, string | number | boolean> = useMemo(() => {
    const attrs = selectedVariant?.attributes ?? {};
    return {
      primaryAttribute: getAttributeLabel(attrs, attributesToDisplay.primaryAttribute) ?? '',
      secondaryAttribute: getAttributeLabel(attrs, attributesToDisplay.secondaryAttribute) ?? '',
      tertiaryAttribute: getAttributeLabel(attrs, attributesToDisplay.tertiaryAttribute) ?? '',
    };
  }, [selectedVariant, attributesToDisplay]);

  const getCurrentSelectionSku = (level: string, selectedValue: string): string | null => {
    currentVariantSelection = {
      ...currentVariantSelection,
      [level]: selectedValue,
    };

    const matchedVariant = product.variants.find((variant) => {
      const attrs = variant.attributes ?? {};

      return (
        (!getAttributeValue(attrs, attributesToDisplay.primaryAttribute) ||
          String(getAttributeLabel(attrs, attributesToDisplay.primaryAttribute))?.toLowerCase() ===
          String(currentVariantSelection.primaryAttribute)?.toLowerCase()) &&
        (!getAttributeValue(attrs, attributesToDisplay.secondaryAttribute) ||
          String(getAttributeLabel(attrs, attributesToDisplay?.secondaryAttribute))?.toLowerCase() ===
          String(currentVariantSelection.secondaryAttribute)?.toLowerCase()) &&
        (currentVariantSelection.tertiaryAttribute !== null && currentVariantSelection.tertiaryAttribute !== undefined
          ? (getAttributeLabel(attrs, attributesToDisplay.tertiaryAttribute) ?? '').toString().toLowerCase() ===
          (currentVariantSelection.tertiaryAttribute ?? '').toString().toLowerCase()
          : false)
      );
    });

    return matchedVariant ? matchedVariant.sku : null;
  };

  const onClickHandler = async (
    event: React.MouseEvent<HTMLButtonElement>,
    level: string,
    selectedValue: string,
    isSelected: boolean,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (isSelected || event.detail > 1) return;

    const skuUrl = getCurrentSelectionSku(level, selectedValue);

    if (skuUrl) {
      const currentVariantIndex = product.variants.findIndex(({ sku }) => sku == skuUrl);
      const currentVariant = product.variants[currentVariantIndex];
      currentVariantUpdate(currentVariant);
    }
  };

  return Object.entries(attributesToDisplay).map(([key, attribute]) => {
    if (getAttributeValue(selectedVariant?.attributes, attribute) === undefined) return null;

    const sortVariantsCmp = (a: Variant, b: Variant) => {
      const first = getAttributeLabel(a?.attributes, attribute);
      const second = getAttributeLabel(b?.attributes, attribute);
      if (first === undefined) return second === undefined ? 0 : -1;
      if (second === undefined) return 1;
      if (typeof first === 'number' && typeof second === 'number') return first - second;
      return String(first).localeCompare(String(second));
    };

    const displayVariantFn = () => {
      const result = discardRepeatedValues(
        product.variants,
        currentVariantSelection,
        attribute,
        key,
        attributesToDisplay,
      );
      result.sort(sortVariantsCmp);
      return result;
    };

    const displayVariants = displayVariantFn();
    const swatchesToDisplay = displayVariants.length > MAX_SWATCHES_TO_DISPLAY
      ? MAX_SWATCHES_TO_DISPLAY - 1
      : MAX_SWATCHES_TO_DISPLAY;

    return (
      <div className="pointer-events-none flex items-end sm:gap-2 px-4" key={`swatch-${key}`}>
        {/* reserve one display slot for the more icon when variants overflow */}
        {displayVariants.slice(0, swatchesToDisplay).map(({ attributes, disabled }, index) => {
          const selectedValue = getAttributeLabel(attributes, attribute)?.toString().toLowerCase().trim() ?? '';
          const isSelected =
            selectedValue.toLowerCase().trim() === String(currentVariantSelection[key]).toLowerCase().trim();

          return (
            <ColorSwatch
              key={`${key}-${index}-${selectedValue}`}
              hexColors={attributes?.['hexcodes'] ?? []}
              isProductTile
              variantSelected={isSelected}
              variantDisabled={disabled}
              onClick={
                displayVariants.length > 1 ? (event) => onClickHandler(event, key, selectedValue, isSelected) : undefined
              }
              value={selectedValue}
              dataFfid={`btn-plpVariant-${key}-${(product?.attributes?.design_name_universal?.trim() ??
                product?.name?.trim()
              )?.replace(/\s+/g, '-')}-${index}`}
            />
          );
        })}
        {/* if more than MAX_SWATCHES_TO_DISPLAY items then show horizontal more icon */}
        {displayVariants.length > MAX_SWATCHES_TO_DISPLAY && (
          <div className="flex size-30 cursor-pointer items-center justify-center" data-ffid={`btn-addmore-${key}`}>
            <HorizontalMore height={20} width={20} />
          </div>
        )}
      </div>
    );
  });
};

export default ColorSwatches;
